import { createDirectStore, } from 'direct-vuex';
import Vue from 'vue';
import Vuex from 'vuex';
import { AddressData, } from '~/utils/models';
import { isDate, isObject, } from '~/utils/models/isObject';
import { initialiseStoreValue, } from '~/utils/store';
import alertModule from '~/utils/store/alert-module';
import globalConfigModule from '~/utils/store/global-config-module';

Vue.use(Vuex);

const UserDetailsStorageKey = 'userDetails';
const UserMeterDetailsStorageKey = 'userMeterDetails';
const MeterReadStoreKey = 'currentMeterReading';

interface UserDetails {
	customerLastName: string;
	customerEmail: string;
	customerNumber: number;
	customerRefNumber: string;
}

interface MeterDetails {
	id: string;
	category: string;
	lastReading: number;
	lastReadDate: Date;
}

export interface UserMeterDetails {
	contractId: string;
	propertyAddress: AddressData;
	meter: MeterDetails;
}

export interface OverlayData {
	navigateTo: string;
	message: string;
}

function isUserDetails(input: unknown): input is UserDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.customerLastName === 'string' &&
		typeof input.customerEmail === 'string' &&
		typeof input.customerNumber === 'number' &&
		typeof input.customerRefNumber === 'string' &&
		true
	);
}

function isMeterDetails(input: unknown): input is MeterDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.id === 'string' &&
		typeof input.category === 'string' &&
		typeof input.lastReading === 'number' &&
		isDate(input.lastReadDate) &&
		true
	);
}

function isUserMeterDetails(input: unknown): input is UserMeterDetails[] {
	return isObject(input) &&
		typeof input.contractId === 'string' &&
		typeof Array.isArray(input.propertyAddress) &&
		isMeterDetails(input.meter);
}

function initialiseUserDetails(): UserDetails | null {
	return initialiseStoreValue(UserDetailsStorageKey, isUserDetails, () => null);
}

function initialiseUserMeterDetails() {
	return initialiseStoreValue(
		UserMeterDetailsStorageKey,
		isUserMeterDetails,
		() => undefined,
		(userInput) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			userInput.meter.lastReadDate = new Date(userInput.meter.lastReadDate);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return userInput;
		}
	);
}

function initialiseMeterRead() {
	return initialiseStoreValue(
		MeterReadStoreKey,
		(v): v is number => typeof v === 'number' && !isNaN(v),
		() => null,
		(v) => parseInt(v, 10)
	);
}

const {
	store,
} = createDirectStore({
	state: {
		currentStep: null as (number | null),
		showSpinner: false,
		userDetails: initialiseUserDetails(),
		meterDetails: initialiseUserMeterDetails(),
		currentMeterReading: initialiseMeterRead(),
		selectedMeterDetails: null as (null | UserMeterDetails),
		isSubmitMeterReadForcefullySucceeded: null as (null | boolean),
		isOverlayVisible: false,
		overlayMessage: '',
		navigateTo: '',
	},
	getters: {
		hasExistingUserDetails(state) {
			return state.userDetails !== null;
		},
		hasExistingMeterDetails(state) {
			return state.meterDetails !== undefined;
		},
	},
	mutations: {
		showSpinner(state) {
			state.showSpinner = true;
		},
		hideSpinner(state) {
			state.showSpinner = false;
		},
		showOverlay(state, data: OverlayData) {
			state.isOverlayVisible = true;
			state.overlayMessage = data.message;
			state.navigateTo = data.navigateTo;
		},
		closeOverlay(state) {
			state.isOverlayVisible = false;
		},
		setStep(state, step: number | null) {
			state.currentStep = step;
		},
		updateUserDetails(state, userDetails: UserDetails) {
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(userDetails));
			state.userDetails = userDetails;
		},
		updateUserMeterDetails(state, userMeterDetails: UserMeterDetails[]) {
			sessionStorage.setItem(UserMeterDetailsStorageKey, JSON.stringify(userMeterDetails));
			state.meterDetails = userMeterDetails;
		},
		setMeterReading(state, meterReading: number) {
			state.currentMeterReading = meterReading;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			sessionStorage.setItem(MeterReadStoreKey, meterReading.toString(10));
		},
		setLatestMeterReading(state, meterReading: number) {
			if (state.selectedMeterDetails) {
				state.selectedMeterDetails.meter.lastReading = meterReading;
				state.selectedMeterDetails.meter.lastReadDate = new Date();
				sessionStorage.setItem(UserMeterDetailsStorageKey, JSON.stringify(state.selectedMeterDetails));
			}
		},
		setSelectedMeterDetails(state, selectedMeterDetails: UserMeterDetails) {
			state.selectedMeterDetails = selectedMeterDetails;
		},
		setSubmitMeterReadForcefullySucceeded(state, boolVal:boolean) {
			state.isSubmitMeterReadForcefullySucceeded = boolVal;
		},
		reset(state) {
			sessionStorage.removeItem(UserDetailsStorageKey);
			sessionStorage.removeItem(UserMeterDetailsStorageKey);
			sessionStorage.removeItem(MeterReadStoreKey);
			state.userDetails = initialiseUserDetails();
			state.meterDetails = initialiseUserMeterDetails();
			state.currentMeterReading = initialiseMeterRead();
			state.selectedMeterDetails = null;
			state.isSubmitMeterReadForcefullySucceeded = null;
		},
	},
	modules: {
		alertModule,
		globalConfigModule,
	},
});

export const Store = store;
